import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ColumnDef, ColumnSizingState } from "@tanstack/react-table";
import { Edit, Trash2, Eye, HardDrive, Users } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { DataTable } from "@/components/ui/data-table";
import { StatusCell, DateTimeCell, SortableHeader } from "@/components/ui/table-cells";
import { TagIcon } from "./TagIcon";
import { CustomerBadge } from "@/components/ui/customer-badge";
import { getCustomerNames } from "@/utils/customerLookup";
import { useTagResourceCounts } from "@/hooks/useTagResourceCounts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Tag {
  id: string;
  name: string;
  description: string;
  type: string;
  color: string;
  icon?: string;
  criteria: any;
  created_at: string;
  created_by: string;
  customers: string[];
}

export interface TagListRef {
  refreshTags: () => void;
}

interface TagListProps {
  searchQuery?: string;
}

const ResourceCounts = ({ tagId }: { tagId: string }) => {
  const { counts, isLoading } = useTagResourceCounts(tagId);
  
  if (isLoading) {
    return <span className="text-muted-foreground">Loading...</span>;
  }
  
  return (
    <div className="flex items-center gap-2 text-sm">
      <div className="flex items-center gap-1">
        <HardDrive className="h-3 w-3 text-muted-foreground" />
        <span>{counts.devices}</span>
      </div>
      <div className="flex items-center gap-1">
        <Users className="h-3 w-3 text-muted-foreground" />
        <span>{counts.total}</span>
      </div>
    </div>
  );
};

export const TagList = forwardRef<TagListRef, TagListProps>(({ searchQuery = "" }, ref) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [deletingTagId, setDeletingTagId] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchTags = async () => {
    try {
      const { data, error } = await supabase
        .from("tags")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setTags(data || []);
    } catch (error) {
      console.error("Error fetching tags:", error);
      toast({
        title: "Error",
        description: "Failed to fetch tags. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTag = async (id: string) => {
    try {
      const { error } = await supabase
        .from("tags")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setTags(tags.filter(tag => tag.id !== id));
      toast({
        title: "Tag deleted",
        description: "Tag has been deleted successfully.",
      });
    } catch (error) {
      console.error("Error deleting tag:", error);
      toast({
        title: "Error",
        description: "Failed to delete tag. Please try again.",
        variant: "destructive",
      });
    }
  };

  useImperativeHandle(ref, () => ({
    refreshTags: fetchTags,
  }));

  useEffect(() => {
    fetchTags();
  }, []);

  const formatCriteria = (criteria: any) => {
    if (!criteria) return "N/A";
    
    try {
      const logic = criteria.and ? "AND" : "OR";
      const conditions = criteria.and || criteria.or || [];
      
      if (conditions.length === 0) return "No conditions";
      
      return `${logic}: ${conditions.length} condition${conditions.length > 1 ? 's' : ''}`;
    } catch {
      return "Invalid criteria";
    }
  };

  const columns: ColumnDef<Tag>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <TagIcon
            iconName={row.original.icon}
            color={row.original.color}
            size={12}
          />
          <Link 
            to={`/tags/${row.original.id}`}
            className="font-medium hover:text-primary underline-offset-4 hover:underline"
          >
            {row.getValue("name")}
          </Link>
        </div>
      ),
    },
    {
      accessorKey: "type",
      header: ({ column }) => <SortableHeader column={column}>Type</SortableHeader>,
      cell: ({ row }) => (
        <StatusCell 
          status={row.getValue("type")}
        />
      ),
    },
    {
      accessorKey: "description",
      header: ({ column }) => <SortableHeader column={column}>Description</SortableHeader>,
      cell: ({ row }) => (
        <div className="max-w-xs truncate">
          {row.getValue("description") || "No description"}
        </div>
      ),
    },
    {
      accessorKey: "criteria",
      header: "Criteria",
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground">
          {formatCriteria(row.getValue("criteria"))}
        </div>
      ),
    },
    {
      accessorKey: "customers",
      header: "Customers",
      cell: ({ row }) => {
        const customers = row.getValue("customers") as string[];
        const customerNames = getCustomerNames(customers || []);
        return (
          <div className="flex flex-wrap gap-1">
            {customerNames.length > 0 ? (
              customerNames.slice(0, 2).map((customerName, index) => (
                <CustomerBadge key={index} customer={customerName} />
              ))
            ) : (
              <Badge variant="outline" className="text-muted-foreground text-xs">
                None
              </Badge>
            )}
            {customerNames.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{customerNames.length - 2} more
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "resource_counts",
      header: "Resources",
      cell: ({ row }) => {
        const tagId = row.original.id;
        return <ResourceCounts tagId={tagId} />;
      },
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
      cell: ({ row }) => (
        <DateTimeCell date={row.getValue("created_at")} format="date" />
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <ActionsDropdown
          actions={[
            {
              label: "View",
              icon: Eye,
              onClick: () => window.location.href = `/tags/${row.original.id}`,
            },
            {
              label: "Edit",
              icon: Edit,
              onClick: () => {
                // TODO: Implement edit functionality
              },
            },
            {
              label: "Delete",
              icon: Trash2,
              onClick: () => setDeletingTagId(row.original.id),
              destructive: true,
            },
          ]}
        />
      ),
    },
  ];

  // Filter tags based on search query
  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tag.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tag.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const deletingTag = tags.find(tag => tag.id === deletingTagId);

  return (
    <>
      <DataTable
        data={filteredTags}
        columns={columns}
        enableRowSelection={true}
        enableColumnResizing={true}
        enableSorting={true}
        columnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        emptyMessage={
          tags.length === 0 
            ? "No tags created yet. Create your first tag to start organizing devices."
            : "No tags match your search. Try adjusting your search criteria."
        }
      />

      <AlertDialog open={!!deletingTagId} onOpenChange={(open) => !open && setDeletingTagId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tag</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingTag && (
                <>
                  Are you sure you want to delete the tag <strong>"{deletingTag.name}"</strong>? 
                  This action cannot be undone and will remove all associations with devices, profiles, applications, and configurations.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingTagId) {
                  deleteTag(deletingTagId);
                  setDeletingTagId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});